
// 'off': no debug logs; 'warn': warnings only; 'all': all debug logs.
// Boolean false/true are also accepted as off/all. Operational errors are separate.
const LDT_DEBUG = 'warn';
function ldtDebug(event, details, level = 'log') {
    if (LDT_DEBUG === 'off' || LDT_DEBUG === false) return;
    if (LDT_DEBUG === 'warn' && level !== 'warn') return;
    const output = level === 'warn' ? console.warn : console.log;
    output.call(console, '[LDT ' + event + ']', {
        time: performance.now().toFixed(1),
        ...(typeof details === 'function' ? details() : details),
    });
}

function ldtSessionError(session) {
    return session && session.readError || Object.assign(new Error('Drone connection closed. Reconnect before trying again.'), { code: 'LDT_CANCELLED' });
}
function createLDTCommand(session, executor) {
    if (!session || session.status !== 'open') return Promise.reject(ldtSessionError(session));
    return new Promise((resolve, reject) => {
        const settle = (error, value) => {
            if (!session.listeners.has(abort)) return;
            session.listeners.delete(abort);
            if (error) reject(error); else resolve(value);
        };
        const abort = error => settle(error);
        session.listeners.add(abort);
        try {
            const result = executor(value => settle(null, value), error => settle(error), session);
            if (result && typeof result.catch === 'function') result.catch(abort);
        } catch (err) { abort(err); }
    });
}
function scheduleLDTCommand(session, callback, milliseconds) {
    if (!session || session.status !== 'open') return;
    const timer = setTimeout(() => {
        session.timers.delete(timer);
        if (session.status === 'open') callback();
    }, milliseconds);
    session.timers.add(timer);
    return timer;
}


// Shared by the modal library and separately evaluated Skulpt modules.
function getLDTSerialLifecycle() {
    if (window.__ldtSerialLifecycle) return window.__ldtSerialLifecycle;
    let tail = Promise.resolve();
    const sessions = new WeakMap();
    const run = operation => {
        const result = tail.then(operation);
        tail = result.catch(() => {});
        return result;
    };
    const stopOperations = (session, error) => {
        for (const listener of [...session.listeners]) listener(error);
        for (const timer of session.timers) clearTimeout(timer);
        session.timers.clear();
    };
    const fail = (session, originalError) => {
        if (!session || session.status !== 'open') return;
        const error = new Error('Drone serial reception failed: ' + originalError.message + '. Reconnect before trying again.');
        error.code = 'LDT_READ_FAILED';
        error.cause = originalError;
        session.readError = error;
        session.status = 'failed';
        stopOperations(session, error);
        // Never await this cleanup inside the receive loop: it joins that loop.
        state.disconnect(session).catch(err => console.error('Drone failed-session cleanup:', err));
    };
    const write = (session, bytes) => {
        if (!session || session.status !== 'open' || session.closing) return Promise.reject(ldtSessionError(session));
        const result = session.writes.then(async () => {
            if (session.status !== 'open' || session.closing) throw ldtSessionError(session);
            if (session.readError) throw session.readError;
            if (session.closed || window.serialport !== session.port) throw new Error('Drone serial connection was replaced.');
            const writer = session.port.writable.getWriter();
            try { await writer.write(bytes); } finally { writer.releaseLock(); }
        });
        session.writes = result.catch(() => {});
        return result;
    };
    const close = async (session, commands = []) => {
        if (!session || session.closed) return;
        session.closing = true;
        if (session.status !== 'failed') session.status = 'closing';
        stopOperations(session, ldtSessionError(session));
        try {
            await session.writes;
            if (commands.length && session.port.writable) {
                const writer = session.port.writable.getWriter();
                try {
                    for (const bytes of commands) await writer.write(Uint8Array.from(bytes));
                } finally { writer.releaseLock(); }
            }
        } finally {
            try { await session.reader.cancel(); } catch (_) {}
            try {
                await session.loop;
            } finally {
                try { await session.port.close(); }
                finally {
                    session.closed = true;
                    session.status = 'closed';
                    if (sessions.get(session.port) === session) sessions.delete(session.port);
                    if (window.reader === session.reader) window.reader = null;
                    if (window.serialport === session.port) window.serialport = null;
                }
            }
        }
    };
    const state = {
        sessions, run, write, close, fail,
        disconnect(session, commands) {
            if (session) session.closing = true;
            return run(() => close(session, commands));
        }
    };
    window.__ldtSerialLifecycle = state;
    return state;
}


function observeDroneScanBytes(state, value) {
    const recovery = state.scanRecovery;
    if (!state.scanStarted || !recovery || !recovery.accepting || !value || !value.length) return;
    recovery.lastInputAt = performance.now();
    recovery.received = true;
    for (const byte of value) {
        if (recovery.previous2 === 85 && recovery.previous1 === 1 && byte === 0) recovery.sawEnd = true;
        recovery.previous2 = recovery.previous1;
        recovery.previous1 = byte;
    }
}

function waitForDroneScan({ state, write, isConnected, session, resetParser, timeoutMs = 30000 }) {
    if (state.scanStarted) return Promise.reject(new Error('A drone scan is already in progress.'));
    if (!session || session.status !== 'open' || session.closing) return Promise.reject(ldtSessionError(session));
    state.scanStarted = true;
    return new Promise((resolve, reject) => {
        let settled = false;
        let writeComplete = false;
        let pollTimer;
        let retries = 0;
        const recovery = { accepting: false };
        state.scanRecovery = recovery;
        const finish = error => {
            if (settled) return;
            settled = true;
            recovery.accepting = false;
            if (state.scanRecovery === recovery) state.scanRecovery = null;
            state.scanStarted = false;
            session.listeners.delete(finish);
            clearTimeout(timeoutTimer);
            clearTimeout(pollTimer);
            if (error) reject(error); else resolve();
        };
        session.listeners.add(finish);
        // One deadline for the user's entire scan, including recovery.
        const timeoutTimer = setTimeout(() => {
            ldtDebug('SCAN timeout', () => ({
                writeComplete,
                scanStarted: state.scanStarted,
                scanEnd: state.scanEnd,
                resultCount: state.scanData.length,
            }));
            finish(new Error('Drone scan timed out. Please try again.'));
        }, timeoutMs);
        const send = () => {
            writeComplete = false;
            recovery.accepting = false;
            Promise.resolve().then(() => {
                if (settled) return;
                if (session.status !== 'open' || session.closing || !isConnected()) throw ldtSessionError(session);
                Object.assign(recovery, { accepting: true, received: false, sawEnd: false,
                    previous1: null, previous2: null, lastInputAt: 0 });
                return write();
            }).then(() => { if (!settled) writeComplete = true; }).catch(finish);
        };
        const poll = () => {
            if (settled) return;
            if (session.status !== 'open' || session.closing || !isConnected()) {
                finish(ldtSessionError(session));
                return;
            }
            if (writeComplete && state.scanEnd === true) {
                finish();
                return;
            }
            if (writeComplete && recovery.received && recovery.sawEnd &&
                performance.now() - recovery.lastInputAt >= 2000) {
                const fault = { code: 'LDT_SCAN_FRAME_STALL', time: performance.now(),
                    lastInputAt: recovery.lastInputAt, resultCount: state.scanData.length, retry: retries };
                state.lastScanRecovery = fault;
                ldtDebug('SCAN stalled framing', fault, 'warn');
                if (retries >= 1) {
                    finish(new Error('Drone scan framing failed after automatic retry. Please try again.'));
                    return;
                }
                retries++;
                recovery.accepting = false;
                try { resetParser(); } catch (error) { finish(error); return; }
                state.scanEnd = false;
                state.scanData = [];
                state.scanItem = false;
                send();
            }
            pollTimer = setTimeout(poll, 20);
        };
        send();
        poll();
    });
}

async function pulseESPReset(port) {
    try {
        await port.setSignals({
            dataTerminalReady: false, // Keep GPIO0 released.
            requestToSend: true,      // Assert reset.
        });

        await new Promise(resolve => setTimeout(resolve, 100));
    } finally {
        // Attempt this even if asserting reset failed:
        // a failed operation might have partially changed the signals.
        await port.setSignals({
            dataTerminalReady: false,
            requestToSend: false,
        });
    }
}

class LocoDroneT {

    constructor () {
        this.bufferSize = 100;

        this.optionNames = ['Accelerometer', 'Battery', 'Temperature', 'Attitude'];

        this.options = {
            'Accelerometer': {refer: 'DATA_ACCEL', count: 3, range: ['min', 'min', 'min'], offset: 0.25,},
            'Battery': {refer: 'DATA_BATTERY', count: 1, range: ['fixed'], fixed: [0, 101], offset: 0.25,},
            'Temperature': {refer: 'DATA_TEMP', count: 1, range: ['min'], offset: 15,},
            'Attitude': {refer: 'DATA_ATTITUDE', count: 3, range: ['min', 'min', 'min'], offset: 0.25,},
        }

        this.TIMEOUT_STR = "timeout";
        this.TIMEOUT_MAX = 3000;
        this.timeout_count = 0;

        this.ldtTimeoutID = null;

        this.can_process = false;

        this.drones = [];
        // For System Endianness
        this.endianFactor = this.get_endian_factor();

        this.read_loop = {
            targetLen: 0,
            targetLenOff: 0,
            header: 0,
            hasTargetLength: false,
            hasHeader: false,
        }

        this.serialSession = null;
        this.readLoopPromise = null;
        
        this.read_buffer = {
            data: [],
        }

        this.sharedData = {
            drones: {
                scanStarted: false,
                scanEnd: false,
                scanData: [],
                scanItem: false,
                connectEnd: false,
                connectState: 0,
                connectFirmware: false,
                receivedData: false,
                waitFlag: false,
                waitData: "",
                messageData: {}
            }
        };
        
        this.monitor_timer_ID = null;
        this.monitor_drone_flag = 0;
        this.CONNECTED_MONITOR = 0;
        this.PLOTTING_MONITOR = 0;
        

        this.battery_last = 0;
        this.temperature_last = 0;
        this.BATTERY_THRESH = 12
        this.TEMP_THRESH = 92

        this.VITALS_MONITOR_TIME_A = 500 // milliseconds

        this.ldt_vitals_str = ""

        this.VITALS_OK = "OK";
        this.VITALS_LOW_BATT = "Low Battery! Please Charge Drone"
        this.VITALS_HIGH_TEMP = "Overheating! Please Power-Down Drone"

        this.self = {

            'DATA_ACCEL': {
                value: "DATA_ACCEL",
                names: ['ax', 'ay', 'az'],
                ylabels: ["AX (g)", "AY (g)", "AZ (g)"],
            },
            'DATA_BATTERY': {
                value: "DATA_BATTERY",
                names: ['battery'],
                ylabels: ["B (%)"],
            },
            'DATA_TEMP': {
                value: "DATA_TEMP",
                names: ['temperature'],
                ylabels: ["T (C)"],
            },
            'DATA_ATTITUDE': {
                value: "DATA_ATTITUDE",
                names: ['pitch', 'roll', 'yaw'],
                ylabels: ["Pitch (deg)", "Roll (deg)", "Yaw (deg)"],
            },
            axArray: [],
            ayArray: [],
            azArray: [],
            battArray: [],
            tempArray: [],
            pitchArray: [],
            rollArray: [],
            yawArray: [],
        };

    }

    clearLDTTimeouts = () => {
        try {
            clearTimeout(this.ldtTimeoutID);
        } catch {}
    }

    clearData = () => {
        this.timeout_count = 0;

        clearTimeout(this.ldtTimeoutID);
        this.ldtTimeoutID = null;
        this.can_process = false;

        this.read_loop = {
            targetLen: 0,
            targetLenOff: 0,
            header: 0,
            hasTargetLength: false,
            hasHeader: false,
        }
        
        this.read_buffer = {
            data: [],
        }

        this.sharedData = {
            drones: {
                scanStarted: false,
                scanEnd: false,
                scanData: [],
                connectEnd: false,
                connectState: 0,
                connectFirmware: false,
                receivedData: false,
                waitFlag: false,
                waitData: "",
                messageData: {}
            }
        };
        this.battery_last = 0;
        this.temperature_last = 0;
    }

    resetArrays = () => {
        this.self.axArray = [];
        this.self.ayArray = [];
        this.self.azArray = [];
        this.self.battArray = [];
        this.self.tempArray = [];
        this.self.pitchArray = [];
        this.self.rollArray = [];
        this.self.yawArray =  [];
    };

    
    set_buffer_size = (buffer_size) => {
        this.bufferSize = buffer_size
    }

    get_buffer_size = () => {
        return this.bufferSize
    }

    // System Endianness Factor For Data Type Conversions
    get_endian_factor = () => {
        let uInt32 = new Uint32Array([0x11223344]);
        let uInt8 = new Uint8Array(uInt32.buffer);
        let endianFactor = 0;
        if (uInt8[0] = 0x11) {
            endianFactor = 3
        }
        return endianFactor;
    }

    waitForStartupDrain = async ({
        minimumMs = 2000,
        quietMs = 300,
        maximumMs = 6000,
    } = {}) => {
        const session = this.serialSession;
        const started = performance.now();

        while (true) {
            if (
                this.serialSession !== session ||
                !session ||
                session.status !== 'open' ||
                session.closing
            ) {
                throw new Error('Serial connection closed during startup.');
            }

            const now = performance.now();
            const lastInput = Math.max(
                started,
                this.lastSerialInputAt ?? started
            );

            if (
                now - started >= minimumMs &&
                now - lastInput >= quietMs
            ) {
                return;
            }

            if (now - started >= maximumMs) {
                throw new Error('Serial startup did not become quiet. Try reconnecting.');
            }

            await new Promise(resolve => setTimeout(resolve, 25));
        }
    }


    writePacket = async (data) => {
        let packet = Uint8Array.from(data);
        await this.webserial_write(packet);
    }

    webserial_write = async (data) => {
        this.can_process = true;
        await getLDTSerialLifecycle().write(this.serialSession, data);
    }

    drone_scan = async () => {
        const state = this.sharedData.drones;
        const reader = window.reader;
        const port = window.serialport;

        if (state.scanStarted) throw new Error('A drone scan is already in progress.');
        state.scanEnd = false;
        state.scanData = [];
        state.scanItem = false;

        await waitForDroneScan({
            state, session: this.serialSession,
            resetParser: () => { this.read_buffer.data = []; this.clear_packet_info(); },
            write: async () => {
                ldtDebug('SCAN send');
                await this.writePacket([TELLO_HEADERS.SSID_LIST_START, 1, 0]);
                ldtDebug('SCAN write complete');
            },
            isConnected: () =>
                !!reader &&
                !!port &&
                window.reader === reader &&
                window.serialport === port &&
                !!port.readable &&
                !!port.writable,
        });

        return "done";
    }

    connect_wifi = async (i) => {
        
        //
        window.ldt_esp_ver = 0 // Reset this

        this.sharedData.drones['connectEnd'] = false;
        this.sharedData.drones['connectState'] = CONNECT_STATES.CONNECT_FAIL;

        let me = this;
        
        return createLDTCommand(this.serialSession, function (resolve, reject, commandSession) {
            
            let drone_ssid = me.sharedData.drones['scanData'][i]
            let packet = [TELLO_HEADERS.TELLO_CONNECT, drone_ssid.length];
            for (let t = 0; t < drone_ssid.length; t++) {
                packet.push(drone_ssid.charCodeAt(t));
            }
            me.writePacket(packet).catch(err => getLDTSerialLifecycle().fail(commandSession, err));  
            
            var m = function () {
                resolve("done");
            }

            var f = function () {
                if (me.sharedData.drones['connectEnd'] === true) {
                    if (me.sharedData.drones['connectState'] !== CONNECT_STATES.CONNECT_OK) {
                        reject("Could Not Connect To The Drone. Please Try Running the Program Again.");
                    } else {
                        scheduleLDTCommand(commandSession, m, 2000);
                    }
                } else {
                    scheduleLDTCommand(commandSession, f, 5);
                }
            }

            scheduleLDTCommand(commandSession, f, 5);
        });
    }


    connect_usb = () => getLDTSerialLifecycle().run(async () => {
        const lifecycle = getLDTSerialLifecycle();
        const previous = window.serialport && lifecycle.sessions.get(window.serialport);
        if (previous) await lifecycle.close(previous);
        else if (window.serialport || window.reader) throw new Error('Stop the existing serial connection through its owner first.');
        this.can_process = false;
        this.read_buffer.data = [];
        const port = await navigator.serial.requestPort({ filters: [{ usbVendorId: 0x10C4 }] });
        await port.open({ baudRate: 115200, bufferSize: 2048 });

        /*try {
            await pulseESPReset(port);
        }  catch (err) {
            console.warn('ESP reset attempt failed; continuing connection:', err);
        }*/

        window.serialport = port;
        const reader = port.readable.getReader();
        window.reader = reader;
        
        this.serialSession = { port, reader, writes: Promise.resolve(), closed: false, closing: false,
            status: 'open', readError: null, listeners: new Set(), timers: new Set() };
        this.readLoopPromise = this.readLoop(reader, this.serialSession);
        this.serialSession.loop = this.readLoopPromise;
        lifecycle.sessions.set(port, this.serialSession);
        this.readLoopPromise.catch(err => console.error('LocoDroneT read loop failed:', err));

        await this.waitForStartupDrain();
        this.read_buffer.data = [];
    });

    connect = async () => {
        try {
            await this.connect_usb();
            await new Promise(resolve => setTimeout(resolve, 2000));
            return 'done';
        } catch (err) {
            console.error('Error creating serial connection', err);
            return 'error';
        }
    }

    webserial_disconnect = () => {
        if (this.serialSession) this.serialSession.closing = true;
        // Resolve this owner's session after any already-queued connection attempt.
        return getLDTSerialLifecycle().run(async () => {
            await getLDTSerialLifecycle().close(this.serialSession);
            
            // Diagnostic cooldown before another queued connection can start.
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            this.can_process = false;
            this.read_buffer.data = [];
        });
    }

    disconnect_usb = async () => {
        await this.webserial_disconnect();
    }

    disconnect = async () => {
        await this.webserial_disconnect();
    }

    disconnect_drone = async () => {

        let packet = [TELLO_HEADERS.TELLO_DISCONNECT, 1, 0];
        await this.writePacket(packet);

        return new Promise(function (resolve, reject) {
            var f = function () {
                resolve("done")
            }
            
            window.setTimeout(f, 50);
        });
    }


    get_data_array = (type) => {
        
        switch(type) {
            case this.self['DATA_ACCEL'].value:
                return [this.self.axArray, this.self.ayArray, this.self.azArray];
            case this.self['DATA_BATTERY'].value:
                return [this.self.battArray];
            case this.self['DATA_TEMP'].value:
                return [this.self.tempArray];
            case this.self['DATA_ATTITUDE'].value:
                return [this.self.pitchArray, this.self.rollArray, this.self.yawArray];
        }
    };

    drone_get_data = async (data_reference) => {

        this.sharedData.drones['receivedData'] = false

        let packet = [TELLO_HEADERS.TELLO_DATA, 1, DATA_TYPES[data_reference]];
        await this.writePacket(packet);
        this.timeout_count = 0
        let me = this

        return createLDTCommand(this.serialSession, function (resolve, reject, commandSession) {
            var f = function () {
                if (me.sharedData.drones['receivedData'] === true) {
                    me.sharedData.drones['receivedData'] = false
                    resolve("done");
                } else {
                    if (me.timeout_count < me.TIMEOUT_MAX) {
                        me.ldtTimeoutID = scheduleLDTCommand(commandSession, f, 5);
                    } else {
                        resolve("failure");//me.TIMEOUT_STR);
                    }                    
                    me.timeout_count += 10;
                }
            }
            scheduleLDTCommand(commandSession, f, 5);
        });
    }

    drone_monitor_vitals = async () => {

        await this.drone_get_data("DATA_BATTERY")
        await this.drone_get_data("DATA_TEMP")

        return "Battery Level (%) " + this.battery_last + ",  Temperature (C): " + this.temperature_last
        //return [this.battery_last, this.temperature_last]
        //return 
    }

    drone_check_vitals = () => {

        if (this.battery_last < this.BATTERY_THRESH) {
            return this.VITALS_LOW_BATT
        } else if (this.temperature_last > this.TEMP_THRESH) {
            return this.VITALS_HIGH_TEMP
        } else {
            return this.VITALS_OK
        }
    }

    get_vitals_timer_ID = () => {
        return this.monitor_timer_ID
    }

    set_vitals_timer_ID = (timer_id) => {
        this.monitor_timer_ID = timer_id
    }

    readLoop = async (reader, session) => {
        try {
            while (session.status === 'open') {
                const output = await this.update_buffer(reader);
                if (output === -1) {
                    if (session.status === 'open') throw new Error('Serial input ended unexpectedly');
                    break;
                }
                if (this.can_process === true) this.process_buffer();
            }
        } catch (err) { getLDTSerialLifecycle().fail(session, err); }
        finally { reader.releaseLock(); }
    }

    update_buffer = async (reader) => {
        const { value, done } = await reader.read();
        // Log before the startup-data discard check.
        ldtDebug('RX', () => ({
            done,
            processingEnabled: this.can_process,
            scanStarted: this.sharedData.drones.scanStarted,
            length: value ? value.length : 0,
            hex: value
                ? Array.from(value, b => b.toString(16).padStart(2, '0')).join(' ')
                : '',
        }));
        if (done) return -1;
        observeDroneScanBytes(this.sharedData.drones, value);

        if (value && value.length) {
            this.lastSerialInputAt = performance.now();
            
            if (this.can_process) {
                this.read_buffer.data = [...this.read_buffer.data, ...value];
            }
        }
        return 0;
    }

    stopReadLoop = async () => {
        const session = this.serialSession;
        if (!session || session.closed) return;
        try { await session.reader.cancel(); } catch (_) {}
        await session.loop;
        if (window.reader === session.reader) window.reader = null;
        this.read_buffer.data = [];
    }

    /*
    readLoop = async() => {
    
        while (true) {
            
            let output = await this.update_buffer();
            if (output === -1) {
                window.reader.releaseLock();
                break;
            } else if (output === -2) {
                try {
                    window.reader = window.serialport.readable.getReader();
                } catch {
                    window.reader.releaseLock();
                    break;                    
                }
            }
            if (this.can_process === true) {
                this.process_buffer();
            }
    
        }
    }

    update_buffer = async () => {
        try {
            const { value, done } = await window.reader.read();

            // Check before accessing or appending value.
            if (done) {
                return -1;
            }

            if (this.can_process === true && value) {
                this.read_buffer.data = [...this.read_buffer.data, ...value];
            }

            return 0;
        } catch (err) {
            // console.log("read error", err);
            return -2;
        }
    }*/

    process_buffer = () => {
        while (this.read_buffer.data.length >= 2) {
            const fullLength = this.read_buffer.data[1] + 2;

            // Keep the partial packet until another read supplies the rest.
            if (this.read_buffer.data.length < fullLength) {
                return;
            }

            const packet = this.read_buffer.data.slice(0, fullLength);
            this.read_buffer.data = this.read_buffer.data.slice(fullLength);

            this.processSerial(packet);
        }
    }

    clear_packet_info = () => {
        this.read_loop.header = 0
        this.read_loop.hasHeader = false
        this.read_loop.targetLen = 0
        this.read_loop.hasTargetLength = false
        this.read_loop.targetLenOff = 0
    }

    processSerial = (dataBytes) => {
        //console.log("serial", dataBytes)
        this.packetFlag = false
        this.packetHaveLen = false
        this.currentLen = 0
        //console.log(dataBytes)
        // Switch on Header Value
        switch (dataBytes[0]) {

            case TELLO_HEADERS.SSID_LIST_ITEM: {
                ldtDebug('parsed scan result', () => ({
                    scanStarted: this.sharedData.drones.scanStarted,
                    hex: Array.from(dataBytes, b => b.toString(16).padStart(2, '0')).join(' '),
                }));
                if (!this.sharedData.drones.scanStarted) break;
                let name = dataBytes.slice(2, 2 + dataBytes[1] - 1);
                name = String.fromCharCode.apply(null, name);
                this.sharedData.drones['scanData'].push(name)
                this.sharedData.drones['scanItem'] = true
                break
            }
            case TELLO_HEADERS.SSID_LIST_END: {
                ldtDebug('parsed scan end', () => ({
                    scanStarted: this.sharedData.drones.scanStarted,
                    hex: Array.from(dataBytes, b => b.toString(16).padStart(2, '0')).join(' '),
                }));
                if (!this.sharedData.drones.scanStarted) break;
                this.sharedData.drones['scanEnd'] = true;
                break
            }
            case TELLO_HEADERS.TELLO_CONNECT: {
                this.sharedData.drones['connectEnd'] = true;
                this.sharedData.drones['connectState'] = dataBytes[2];
                break
            }
            case TELLO_HEADERS.TELLO_DATA: {
                this.process_data(dataBytes[1], dataBytes.slice(2, dataBytes.length));
                break
            }
            case TELLO_HEADERS.TELLO_TAKEOFF: {
                const data = String.fromCharCode.apply(null, dataBytes.slice(2, dataBytes.length));
                this.sharedData.drones['waitData'] = data
                this.sharedData.drones['waitFlag'] = false;
                break
            }
            case TELLO_HEADERS.TELLO_LAND: {
                const data = String.fromCharCode.apply(null, dataBytes.slice(2, dataBytes.length));
                //console.log(data)
                this.sharedData.drones['waitData'] = data
                this.sharedData.drones['waitFlag'] = false;
                break
            }
            case TELLO_HEADERS.TELLO_FLIP: {
                const data = String.fromCharCode.apply(null, dataBytes.slice(2, dataBytes.length));
                this.sharedData.drones['waitData'] = data
                this.sharedData.drones['waitFlag'] = false;
                break
            }
        }
    }
    
    getKeyByValue = (object, value) => {
        return Object.keys(object).find(key => object[key] === value);
    }

    process_data = (data_len, data_packet) => {

        let data = ""
        for (let i = 1; i < data_len; i++) {// - 2; i++) {
            data += String.fromCharCode(data_packet[i])
        }
        
        let data_string = this.getKeyByValue(DATA_TYPES, data_packet[0]);
    
        switch (data_string) {
            case "DATA_BATTERY": {
                if (this.self.battArray.length > this.bufferSize) {    
                    this.self.battArray.splice(0, 1);
                }     
                this.self.battArray.push(parseFloat(data))

                // temporary
                this.battery_last = parseFloat(data)
                //console.log("battery ", data)
                //this.battery_last = data
                break
            }
            case "DATA_BARO": {
                this.sharedData.drones['messageData'] = {"barometer": parseFloat(data)}
                break
            }
            case "DATA_ATTITUDE": {
                let startIndices = []
                let endIndices = []
                for(var i=0; i<data.length;i++) {
                    if (data[i] === ":") startIndices.push(i + 1);
                    if (data[i] === ",") endIndices.push(i);
                    if (data[i] === ";") endIndices.push(i);
                }
                if (this.self.pitchArray.length > this.bufferSize) {    
                    this.self.pitchArray.splice(0, 1);
                    this.self.rollArray.splice(0, 1);
                    this.self.yawArray.splice(0, 1);
                }
                this.self.pitchArray.push(parseFloat(data.slice(startIndices[0], endIndices[0])));
                this.self.rollArray.push(parseFloat(data.slice(startIndices[1], endIndices[1])));
                this.self.yawArray.push(parseFloat(data.slice(startIndices[2], endIndices[2])));
                break
            }
            case "DATA_HEIGHT": {
                let endIndex = data.indexOf('dm')
                this.sharedData.drones['messageData'] = {"height": parseFloat(data.slice(0, endIndex))}
                break
            }
            case "DATA_TEMP": {
                let firstNumIndexEnd = data.indexOf('~')
                let firstNum = parseFloat(data.slice(0, firstNumIndexEnd))
                let secondNum = parseFloat(data.slice(firstNumIndexEnd + 1, data.length - 1))
                let avg_temp = (firstNum + secondNum) / 2
                if (this.self.tempArray.length > this.bufferSize) {    
                    this.self.tempArray.splice(0, 1);
                }
                this.self.tempArray.push(avg_temp)
                
                // temporary
                this.temperature_last = parseFloat((firstNum + secondNum) / 2)
                //console.log("temp ", data)
                //this.temperature_last = data
                break
            }
            case "DATA_SPEED": {
                this.sharedData.drones['messageData'] = {"speed": parseFloat(data)}
                break
            }
            case "DATA_TIME": {
                this.sharedData.drones['messageData'] = {"time": parseFloat(data)}
                break
            }
            case "DATA_ACCEL": {
                if (this.self.axArray.length > this.bufferSize) {    
                    this.self.axArray.splice(0, 1);
                    this.self.ayArray.splice(0, 1);
                    this.self.azArray.splice(0, 1);
                }
                let startIndices = []
                let endIndices = []
                for(var i=0; i<data.length;i++) {
                    if (data[i] === ":") startIndices.push(i + 1);
                    if (data[i] === ";") endIndices.push(i);
                }
                this.self.axArray.push(parseFloat(data.slice(startIndices[0], endIndices[0])) / 1000);
                this.self.ayArray.push(-1.0 * parseFloat(data.slice(startIndices[1], endIndices[1])) / 1000);
                this.self.azArray.push(-1.0 * parseFloat(data.slice(startIndices[2], endIndices[2])) / 1000);
                //this.sharedData.drones['messageData'] = {"accel": data_list}
                break
            }
            case "DATA_TOF": {
                this.sharedData.drones['messageData'] = {"tof": parseFloat(data)}
                break
            }
            case "DATA_WIFI": {
                this.sharedData.drones['messageData'] = {"wifi": parseFloat(data)}
                break
            }
            case "DATA_SDK": {
                this.sharedData.drones['messageData'] = {"sdk": parseFloat(data)}
                break
            }
    
        }
        this.sharedData.drones['receivedData'] = true;
    
    }


    drone_takeoff = async() => {

        var me = this;

        return createLDTCommand(this.serialSession, function(resolve, reject, commandSession) {

            var p = function() {
                let packet = [TELLO_HEADERS.TELLO_RC, 4, 0, 0, 0, 0];
                me.writePacket(packet).catch(err => getLDTSerialLifecycle().fail(commandSession, err));
                me.ldtTimeoutID = setTimeout(r, 100);
            }
            var r = function() {
                let packet = [TELLO_HEADERS.TELLO_TAKEOFF, 1, 0];
                me.writePacket(packet).catch(err => getLDTSerialLifecycle().fail(commandSession, err));    
                me.sharedData.drones['waitFlag'] = true;  
                me.ldtTimeoutID = setTimeout(f, 100);  
            }
            var f = function() {
                if (me.sharedData.drones['waitFlag'] === true) {
                    me.ldtTimeoutID = setTimeout(f, 100);
                } else {
                    me.ldtTimeoutID = setTimeout(m, 100);
                }
            }
            var m = function() {
                let packet = [TELLO_HEADERS.TELLO_RC, 4, 0, 0, 0, 0];
                me.writePacket(packet).catch(err => getLDTSerialLifecycle().fail(commandSession, err));
                me.ldtTimeoutID = setTimeout(h, 100);
            }
            var h = function() {
                resolve("done");
            }

            me.ldtTimeoutID = setTimeout(p, 100);

        });
    }

    drone_land = async() => {
        
        var me = this;

        return createLDTCommand(this.serialSession, function(resolve, reject, commandSession) {

            var p = function() {
                let packet = [TELLO_HEADERS.TELLO_RC, 4, 0, 0, 0, 0];
                me.writePacket(packet).catch(err => getLDTSerialLifecycle().fail(commandSession, err));
                me.ldtTimeoutID = setTimeout(r, 100);
            }
            var r = function() {
                let packet = [TELLO_HEADERS.TELLO_LAND, 1, 0];
                me.writePacket(packet).catch(err => getLDTSerialLifecycle().fail(commandSession, err));    
                me.sharedData.drones['waitFlag'] = true;  
                me.ldtTimeoutID = setTimeout(f, 100);  
            }
            var f = function() {
                if (me.sharedData.drones['waitFlag'] === true) {
                    me.ldtTimeoutID = setTimeout(f, 100);
                } else {
                    me.ldtTimeoutID = setTimeout(m, 100);
                }
            }
            var m = function() {
                let packet = [TELLO_HEADERS.TELLO_RC, 4, 0, 0, 0, 0];
                me.writePacket(packet).catch(err => getLDTSerialLifecycle().fail(commandSession, err));
                me.ldtTimeoutID = setTimeout(h, 100);
            }
            var h = function() {
                resolve("done");
            }

            me.ldtTimeoutID = setTimeout(p, 100);

        });
    }

    drone_flip = async(flip_direction) => {

        var me = this;

        return createLDTCommand(this.serialSession, function(resolve, reject, commandSession) {

            var p = function() {
                let packet = [TELLO_HEADERS.TELLO_FLIP, 1, FLIP_DIRECTIONS[flip_direction]]
                me.writePacket(packet).catch(err => getLDTSerialLifecycle().fail(commandSession, err));
                me.sharedData.drones['waitFlag'] = true;  
                me.ldtTimeoutID = setTimeout(f, 100);  
            }
            var f = function() {
                if (me.sharedData.drones['waitFlag'] === true) {
                    me.ldtTimeoutID = setTimeout(f, 100);
                } else {
                    me.ldtTimeoutID = setTimeout(m, 100);
                }
            }
            var m = function() {
                resolve("done");
            }
            
            me.ldtTimeoutID = setTimeout(p, 100);
        });
    }

    drone_rc = async(a, b, c, d) => {
        
        var me = this;

        return createLDTCommand(this.serialSession, function(resolve, reject, commandSession) {
            var f = function () {
                resolve("done");
            }
            let packet = [TELLO_HEADERS.TELLO_RC, 4, a, b, c, d];
            me.writePacket(packet).catch(err => getLDTSerialLifecycle().fail(commandSession, err));
            me.ldtTimeoutID = setTimeout(f, 50);
        });
    }


}

var FLIP_DIRECTIONS = {
    FLIP_LEFT: 0,
    FLIP_RIGHT: 1,
    FLIP_FORWARD: 2,
    FLIP_BACK: 3
};

var TELLO_HEADERS = {
    SSID_LIST_START: 83,
    SSID_LIST_ITEM: 84,
    SSID_LIST_END: 85,
    TELLO_CONNECT: 67,
    TELLO_DISCONNECT: 68,
    
    TELLO_TAKEOFF: 69,
    TELLO_LAND: 70,
    TELLO_MOVE: 71,
    TELLO_DATA: 72,
    TELLO_HOVER: 73,
    TELLO_YAW: 74,
    TELLO_FLIP: 75,
    TELLO_RC: 76,
    TELLO_CURVE: 77,
    TELLO_GO: 78,

    //TELLO_EMERGENCY: 101,
};

var DATA_TYPES = {
    DATA_BATTERY: 0,
    DATA_BARO: 1,
    DATA_ATTITUDE: 2,
    DATA_HEIGHT: 3,
    DATA_TEMP: 4,
    DATA_SPEED: 5,
    DATA_TIME: 6, 
    DATA_ACCEL: 7,
    DATA_TOF: 8,
    DATA_WIFI: 9,
    DATA_SDK: 10
};


var CONNECT_STATES = {
    CONNECT_FAIL: 0,
    CONNECT_OK: 1 
};


export default LocoDroneT;
